import { FormattedMessage, useIntl } from 'react-intl';
import { type ReactNode, useMemo } from 'react';
import { FormattedMoney, useMoneyFormatter } from '@repo/design-system-kit';
import { oppositeSign } from '../../../utils';
import type { Document } from '../../types';
import { usePdfPreviewContext } from '../../pdf-preview-context.tsx';
import styles from './totals.module.css';

interface TotalsProps {
  document: Document;
}

export function Totals({ document }: TotalsProps): ReactNode {
  const { formatMessage, formatNumber } = useIntl();
  const { formatMoney } = useMoneyFormatter();
  const { displayLanguage, currency, type, variant, isPartialInvoice } = usePdfPreviewContext();

  const showSubtotalsVatRates =
    document.displayEachVatSubtotals && (type === 'invoice' || type === 'quote');

  const showDepositAmount =
    document.depositAmount && !isPartialInvoice && !document.isPartialCreditNote;

  const discountLabel = useMemo(() => {
    if (document.discount?.type === 'absolute') {
      return formatMessage({ id: 'pdf-preview.amount-discount' }, { language: displayLanguage });
    }

    return formatMessage(
      { id: 'pdf-preview.total-discount' },
      {
        language: displayLanguage,
        discountPercentage: formatNumber(Number(document.discount?.value) || 0, {
          style: 'percent',
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        }),
      }
    );
  }, [
    displayLanguage,
    document.discount?.type,
    document.discount?.value,
    formatMessage,
    formatNumber,
  ]);

  const withholdingTaxAmount = useMemo(() => {
    const amount = Math.abs(Number(document.withholdingTaxAmount));
    return type === 'credit-note' ? amount : -amount;
  }, [document.withholdingTaxAmount, type]);

  const withholdingTaxLabel = useMemo(() => {
    if (variant === 'ES') {
      return formatMessage(
        { id: 'pdf-preview.es-withholding-tax' },
        {
          language: displayLanguage,
          irpfRate: formatNumber(-Number(document.withholdingTax?.rate) || 0, {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          }),
        }
      );
    }

    return formatMessage({ id: 'pdf-preview.withholding-tax' }, { language: displayLanguage });
  }, [displayLanguage, document.withholdingTax?.rate, formatMessage, formatNumber, variant]);

  const hasDiscount = document.discount && document.totalDiscount !== '0.00';

  return (
    <div>
      <table className={styles.totals} data-testid="pdf-preview-totals">
        <tbody>
          {!hasDiscount && document.previouslyInvoicedAmount ? (
            <tr data-testid="pdf-preview-total-progress">
              <td className="body-1-bold">
                <FormattedMessage
                  id="pdf-preview.previously-invoiced-amount"
                  values={{ language: displayLanguage }}
                />
              </td>
              <td className="body-1">
                <FormattedMoney
                  amount={oppositeSign(Number(document.previouslyInvoicedAmount))}
                  currency={currency}
                  token="money-symbol"
                />
              </td>
            </tr>
          ) : null}

          <tr data-testid="pdf-preview-total-excl-vat">
            <td className="body-1-bold">
              <FormattedMessage
                id="pdf-preview.total-without-vat"
                values={{ language: displayLanguage }}
              />
            </td>
            <td className="body-1">
              <FormattedMoney
                amount={document.totalExcludingVat ?? 0}
                currency={currency}
                token="money-symbol"
              />
            </td>
          </tr>

          {hasDiscount ? (
            <>
              <tr data-testid="pdf-preview-total-discount">
                <td className="body-1">{discountLabel}</td>
                <td className="body-1">
                  <FormattedMoney
                    amount={oppositeSign(Number(document.totalDiscount))}
                    currency={currency}
                    token="money-symbol"
                  />
                </td>
              </tr>

              {document.previouslyInvoicedAmount ? (
                <tr data-testid="pdf-preview-total-progress">
                  <td className="body-1-bold">
                    <FormattedMessage
                      id="pdf-preview.previously-invoiced-amount"
                      values={{ language: displayLanguage }}
                    />
                  </td>
                  <td className="body-1">
                    <FormattedMoney
                      amount={oppositeSign(Number(document.previouslyInvoicedAmount))}
                      currency={currency}
                      token="money-symbol"
                    />
                  </td>
                </tr>
              ) : null}

              <tr data-testid="pdf-preview-total-after-discount">
                <td className="body-1-bold">
                  <FormattedMessage
                    id="pdf-preview.total-after-discount"
                    values={{ language: displayLanguage }}
                  />
                </td>
                <td className="body-1">
                  <FormattedMoney
                    amount={document.discountedTotalExcludingVat ?? 0}
                    currency={currency}
                    token="money-symbol"
                  />
                </td>
              </tr>
            </>
          ) : null}

          {document.welfareFundAmount && document.welfareFundAmount !== '0.00' ? (
            <tr data-testid="pdf-preview-pension-contribution">
              <td className="body-1-bold">
                <FormattedMessage
                  id="pdf-preview.pension-contribution"
                  values={{ language: displayLanguage }}
                />
              </td>
              <td className="body-1">
                <FormattedMoney
                  amount={document.welfareFundAmount}
                  currency={currency}
                  token="money-symbol"
                />
              </td>
            </tr>
          ) : null}

          {showSubtotalsVatRates && document.vatSubtotals ? (
            <>
              {document.vatSubtotals.map((subtotal, index) => (
                <tr
                  data-testid={`pdf-preview-vat-subtotal-${index}`}
                  //eslint-disable-next-line react/no-array-index-key -- no unique id
                  key={`vat-subtotals-${index}`}
                >
                  <td className="body-1">
                    <FormattedMessage
                      id="pdf-preview.vat-subtotal"
                      values={{
                        language: displayLanguage,
                        VATRate: formatNumber(Number(subtotal.rate), {
                          style: 'percent',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 1,
                        }),
                        subtotalPerVATPrice: formatMoney({
                          amount: subtotal.totalExcludingVat,
                          currency,
                          token: 'money-symbol',
                        }),
                      }}
                    />
                  </td>
                  <td className="body-1">
                    <FormattedMoney
                      amount={subtotal.vatTotal}
                      currency={currency}
                      token="money-symbol"
                    />
                  </td>
                </tr>
              ))}
            </>
          ) : null}

          <tr data-testid="pdf-preview-total-vat">
            <td className="body-1-bold">
              <FormattedMessage
                id="pdf-preview.total-vat-amount"
                values={{ language: displayLanguage }}
              />
            </td>
            <td className="body-1">
              <FormattedMoney
                amount={document.totalVat ?? 0}
                currency={currency}
                token="money-symbol"
              />
            </td>
          </tr>

          {document.withholdingTaxAmount && document.withholdingTaxAmount !== '0.00' ? (
            <tr data-testid="pdf-preview-withholding-tax">
              <td className="body-1-bold">{withholdingTaxLabel}</td>
              <td className="body-1">
                <FormattedMoney
                  amount={withholdingTaxAmount}
                  currency={currency}
                  token="money-symbol"
                />
              </td>
            </tr>
          ) : null}

          {document.stampDutyAmount && document.stampDutyAmount !== '0.00' ? (
            <tr data-testid="pdf-preview-revenue-stamp">
              <td className="body-1-bold">
                <FormattedMessage
                  id="pdf-preview.revenue-stamp"
                  values={{ language: displayLanguage }}
                />
              </td>
              <td className="body-1">
                <FormattedMoney
                  amount={document.stampDutyAmount}
                  currency={currency}
                  token="money-symbol"
                />
              </td>
            </tr>
          ) : null}

          <tr className={styles['total-amount']} data-testid="pdf-preview-total-incl-vat">
            <td className="body-1-bold">
              <FormattedMessage id="pdf-preview.total-vat" values={{ language: displayLanguage }} />
            </td>
            <td className="body-1-bold">
              <FormattedMoney
                amount={document.totalAmount ?? 0}
                currency={currency}
                token="money-symbol"
              />
            </td>
          </tr>

          {showDepositAmount ? (
            <>
              <tr data-testid="pdf-preview-deposit-amount">
                <td className="body-1-bold">
                  <FormattedMessage
                    id="pdf-preview.total-deposit"
                    values={{ language: displayLanguage }}
                  />
                </td>
                <td className="body-1">
                  <FormattedMoney
                    amount={
                      type === 'credit-note'
                        ? Number(document.depositAmount)
                        : oppositeSign(Number(document.depositAmount))
                    }
                    currency={currency}
                    token="money-symbol-sign-decimal"
                  />
                </td>
              </tr>

              {document.totalAmountDue ? (
                <tr data-testid="pdf-preview-amount-due">
                  <td className="body-1-bold">
                    <FormattedMessage
                      id="pdf-preview.total-total-amount-due"
                      values={{ language: displayLanguage }}
                    />
                  </td>
                  <td className="body-1-bold">
                    <FormattedMoney
                      amount={document.totalAmountDue}
                      currency={currency}
                      token="money-symbol"
                    />
                  </td>
                </tr>
              ) : null}
            </>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
